import { useState, useEffect, useMemo, useCallback } from 'react';
import { union } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@hotjar/navigation';
import { useHighlightsSuggestions } from '@hotjar/webapp/features/Activation/hooks/useHighlightsSuggestions';
import type { Site } from '@hotjar/site';
import LockImage from '@hotjar/shared-assets/images/lock.png';
import { DETAILS } from '@hotjar/webapp/constants/routes/profile';
import { TileIds as TILES } from '@hotjar/webapp/domains/activation/start-page/types';
import {
  useStartPageContent,
  invalidateStartPageContentQuery,
} from '@hotjar/webapp/domains/activation/start-page';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { getSalesLink } from '@hotjar/upsell';
import TeamImage from './assets/team.png';
import SlackImage from './assets/slack.png';
import HighlightsImage from './assets/highlights.png';
import EventsImage from './assets/events.png';
import HelpImage from './assets/help.png';
import UserAttributesImage from './assets/user-attributes.png';
import ZapierImage from './assets/zapier.png';
import GoogleAnalyticsImage from './assets/google-analytics.png';
import TrackingConsoleErrorsImage from './assets/tracking-console-errors.png';
import { useDismissTile } from './useDismissTile';
import { useSuggestionsFilters } from './useSuggestionsFilters';

export interface Suggestion {
  id: TILES;
  href: string;
  image: string;
  title: string;
  isExternalLink?: boolean;
  description: string;
}

interface Suggestions {
  suggestions: Suggestion[];
  dismiss: (id: string, silent?: boolean) => void;
}

export const useSuggestions = (site: Site): Suggestions => {
  const { t } = useTranslation();
  const { highlightsLink } = useHighlightsSuggestions(site);
  const { data: startPageContent } = useStartPageContent();
  const { buildHrefFromRoute, routes } = useNavigation();
  const { suggestionsFilters } = useSuggestionsFilters(site);

  const getFilteredStartPageContent = useCallback(
    () => startPageContent?.tiles?.filter(suggestionsFilters),
    [startPageContent?.tiles, suggestionsFilters],
  );

  const { dismiss: dismissBookCallTile } = useDismissTile(TILES.BOOK_CALL);
  const { dismiss: dismissTeamTile } = useDismissTile(TILES.TEAM);
  const { dismiss: dismissSlackTile } = useDismissTile(TILES.SLACK);
  const { dismiss: dismissHighlightsTile } = useDismissTile(TILES.HIGHLIGHTS);
  const { dismiss: dismissEventsTile } = useDismissTile(TILES.EVENTS);
  const { dismiss: dismissUserAttributesTile } = useDismissTile(TILES.USER_ATTRIBUTES);
  const { dismiss: dismissMFASuggestion } = useDismissTile(TILES.ACTIVATE_MFA);
  const { dismiss: dismissZapierTile } = useDismissTile(TILES.ZAPIER);
  const { dismiss: dismissGoogleAnalyticsTile } = useDismissTile(TILES.GOOGLE_ANALYTICS);
  const { dismiss: dismissErrorReporting } = useDismissTile(TILES.ERROR_REPORTING);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const urlVisibleTiles = useMemo(
    () =>
      Object.values(TILES).filter(
        (tile) => !!getParameter(window.location.href, `force_${tile}_tile`),
      ),
    [],
  );

  const dismiss = async (tile: string, silent = false) => {
    switch (tile) {
      case TILES.BOOK_CALL:
        await dismissBookCallTile(silent);
        break;
      case TILES.TEAM:
        await dismissTeamTile(silent);
        break;
      case TILES.SLACK:
        await dismissSlackTile(silent);
        break;
      case TILES.HIGHLIGHTS:
        await dismissHighlightsTile(silent);
        break;
      case TILES.EVENTS:
        await dismissEventsTile(silent);
        break;
      case TILES.USER_ATTRIBUTES:
        await dismissUserAttributesTile(silent);
        break;
      case TILES.ACTIVATE_MFA:
        await dismissMFASuggestion(silent);
        break;
      case TILES.ZAPIER:
        await dismissZapierTile(silent);
        break;
      case TILES.GOOGLE_ANALYTICS:
        await dismissGoogleAnalyticsTile(silent);
        break;
      case TILES.ERROR_REPORTING:
        await dismissErrorReporting(silent);
        break;
      default:
        break;
    }

    invalidateStartPageContentQuery();
  };

  const TILES_MAP: { [key in TILES]: Suggestion } = useMemo(
    () => ({
      [TILES.BOOK_CALL]: {
        id: TILES.BOOK_CALL,
        title: t('activation:space-overview.suggestions.book-call.title'),
        description: t('activation:space-overview.suggestions.book-call.description'),
        image: HelpImage,
        isExternalLink: true,
        href: getSalesLink('InAppTile'),
      },
      [TILES.TEAM]: {
        id: TILES.TEAM,
        title: t('activation:space-overview.suggestions.team.title'),
        description: t('activation:space-overview.suggestions.team.description'),
        image: TeamImage,
        href: routes.TEAM?.INVITE?.path ? buildHrefFromRoute(routes.TEAM.INVITE.path) : '',
      },
      [TILES.SLACK]: {
        id: TILES.SLACK,
        title: t('activation:space-overview.suggestions.slack.title'),
        description: t('activation:space-overview.suggestions.slack.description'),
        image: SlackImage,
        isExternalLink: true,
        href: buildHrefFromRoute(routes.SETTINGS?.INTEGRATION_DETAILS.path, {
          siteId: site.id,
          integrationName: 'slack',
        }),
      },
      [TILES.HIGHLIGHTS]: {
        id: TILES.HIGHLIGHTS,
        title: t('activation:space-overview.suggestions.highlights.title'),
        description: t('activation:space-overview.suggestions.highlights.description'),
        image: HighlightsImage,
        href: highlightsLink,
        isExternalLink: true,
      },
      [TILES.EVENTS]: {
        id: TILES.EVENTS,
        title: t('activation:space-overview.suggestions.events.title'),
        description: t('activation:space-overview.suggestions.events.description'),
        image: EventsImage,
        href: 'https://help.hotjar.com/hc/en-us/articles/4413876230423-What-are-Events-',
        isExternalLink: true,
      },
      [TILES.USER_ATTRIBUTES]: {
        id: TILES.USER_ATTRIBUTES,
        title: t('activation:space-overview.suggestions.user-attributes.title'),
        description: t('activation:space-overview.suggestions.user-attributes.description'),
        image: UserAttributesImage,
        href: routes.SETTINGS?.USER_ATTRIBUTES?.path
          ? `${buildHrefFromRoute(routes.SETTINGS?.USER_ATTRIBUTES.path)}?site-id=${site.id}`
          : '',
      },
      [TILES.ACTIVATE_MFA]: {
        id: TILES.ACTIVATE_MFA,
        title: t('activation:space-overview.suggestions.activate-mfa.title'),
        description: t('activation:space-overview.suggestions.activate-mfa.description'),
        image: LockImage,
        href: `${DETAILS.path}?mfaModal=1`,
      },
      [TILES.ZAPIER]: {
        id: TILES.ZAPIER,
        title: t('activation:space-overview.suggestions.zapier.title'),
        description: t('activation:space-overview.suggestions.zapier.description'),
        image: ZapierImage,
        href: buildHrefFromRoute(routes.SETTINGS?.INTEGRATION_DETAILS.path, {
          siteId: site.id,
          integrationName: 'zapier',
        }),
      },
      [TILES.GOOGLE_ANALYTICS]: {
        id: TILES.GOOGLE_ANALYTICS,
        title: t('activation:space-overview.suggestions.google-analytics.title'),
        description: t('activation:space-overview.suggestions.google-analytics.description'),
        image: GoogleAnalyticsImage,
        href: buildHrefFromRoute(routes.SETTINGS?.INTEGRATION_DETAILS.path, {
          siteId: site.id,
          integrationName: 'google-analytics',
        }),
      },
      [TILES.ERROR_REPORTING]: {
        id: TILES.ERROR_REPORTING,
        title: t('activation:space-overview.suggestions.error-reporting.title'),
        description: t('activation:space-overview.suggestions.error-reporting.description'),
        image: TrackingConsoleErrorsImage,
        href: `${buildHrefFromRoute(routes.SITE?.SETTINGS.path, {
          siteId: site.id,
        })}#targeting`,
      },
    }),
    [
      highlightsLink,
      buildHrefFromRoute,
      routes.SETTINGS?.INTEGRATION_DETAILS.path,
      routes.SETTINGS?.USER_ATTRIBUTES.path,
      routes.SITE?.SETTINGS.path,
      routes.TEAM.INVITE.path,
      site.id,
      t,
    ],
  );

  useEffect(() => {
    const allVisibleTiles = union(getFilteredStartPageContent(), urlVisibleTiles);
    if (allVisibleTiles.length) {
      setSuggestions(allVisibleTiles.map((tileId) => TILES_MAP[tileId]));
    } else {
      setSuggestions([]);
    }
  }, [TILES_MAP, startPageContent?.tiles, urlVisibleTiles, getFilteredStartPageContent]);

  return {
    suggestions,
    dismiss,
  };
};
